import { createEnv } from "@t3-oss/env-nextjs";
import { z } from "zod";

export const env = createEnv({
  /**
   * Specify your server-side environment variables schema here. This way you can ensure the app
   * isn't built with invalid env vars.
   */
  server: {
    SENTRY_DSN: z.string().optional(),
    DATABASE_URL: z.string().url(),
    DATABASE_CA_CERT: z.string().optional(),
    DATABASE_CA_CERT_PATH: z.string().optional(),
    DATABASE_SSL: z.enum(["true", "false"]).optional().default("false"),
    NODE_ENV: z
      .enum(["development", "test", "production"])
      .default("development"),
    BETTER_AUTH_SECRET: z.string(),
    BETTER_AUTH_URL: z.string().url().optional(),
    GOOGLE_CLIENT_ID: z.string(),
    GOOGLE_CLIENT_SECRET: z.string(),
    STRIPE_SECRET_KEY: z.string(),
    STRIPE_PUBLISHABLE_KEY: z.string(),
    STRIPE_WEBHOOK_SECRET: z.string(),
    STRIPE_PRICE_SMALL: z.string(),
    STRIPE_PRICE_STANDARD: z.string(),
    STRIPE_PRICE_LARGE: z.string(),
    SMTP_HOST: z.string().optional(),
    SMTP_PORT: z.string().optional(),
    SMTP_SECURE: z.string().optional(),
    SMTP_USER: z.string().optional(),
    SMTP_PASS: z.string().optional(),
    SMTP_FROM: z.string().optional(),
    CONTACT_PRO_EMAIL: z.string().email().optional(),
    EMAIL_RATE_LIMIT: z.string().optional().default("50"),
    APP_URL: z.string().url().optional(),
    CRON_SECRET: z.string().optional(),
    EMAIL_REPLY_DOMAIN: z.string().optional(), // Domaine pour les réponses email (ex: reply.coproflow.fr)
    INVOICE_EMAIL_DOMAIN: z.string().optional(), // Domaine pour les factures reçues par email (ex: coproflow.fr)
    EMAIL_REPLY_API_KEY: z.string().optional(), // Clé API pour l'endpoint de réponse par email
    AI_SUMMARY_RATE_LIMIT_MINUTES: z.string().optional().default("5"),
    AI_SUMMARY_DAILY_LIMIT: z.string().optional().default("20"),
    AI_SUMMARY_MIN_POSTS: z.string().optional().default("3"),
    MAX_EXTERNAL_SUBSCRIBERS_PER_TOPIC: z.string().optional().default("10"), // Limite d'abonnés externes par topic
    GUEST_VERIFY_TOKEN_EXPIRY_HOURS: z.string().optional().default("48"), // Expiration du token de vérification (heures)

    // AWS S3 / OVH Object Storage
    S3_REGION: z.string().default("gra"), // Région S3 (OVH: gra, sbg, etc.)
    S3_ENDPOINT: z.string().url(), // Endpoint S3 (ex: https://s3.gra.io.cloud.ovh.net)
    S3_ACCESS_KEY_ID: z.string(), // Access Key ID S3
    S3_SECRET_ACCESS_KEY: z.string(), // Secret Access Key S3
    S3_BUCKET: z.string().default("notreimmeuble-documents"), // Nom du bucket S3
    S3_PUBLIC_URL: z.string().url().optional(), // URL publique du bucket (CDN)

    // OVH AI Endpoints
    OVH_AI_ENDPOINTS_EMBEDDING_MODEL_URL: z.string().url(), // URL de l'endpoint BGE-M3 pour embeddings
    OVH_AI_ENDPOINTS_ACCESS_TOKEN: z.string(), // Token d'accès OVH AI

    // OCR Provider Selection (unstructured vs docling)
    OCR_PROVIDER: z
      .enum(["docling", "unstructured"])
      .optional()
      .default("unstructured"),

    // Unstructured (Default OCR provider)
    UNSTRUCTURED_API_URL: z.string().url().optional(),
    UNSTRUCTURED_API_TOKEN: z.string().optional(),
    UNSTRUCTURED_WEBHOOK_SECRET: z.string().optional(),

    // Docling (IBM) - Alternative OCR provider (more resource-intensive)
    DOCLING_API_URL: z.string().url().optional(),
    DOCLING_API_TOKEN: z.string().optional(),
    DOCLING_WEBHOOK_SECRET: z.string().optional(),
    DOCLING_TIMEOUT_MS: z.coerce
      .number()
      .int()
      .positive()
      .optional()
      .default(30000),

    // Reranker Service
    RERANKER_URL: z.string().url().optional(),
    RERANKER_API_TOKEN: z.string().optional(),

    // Redis (Cache & Rate Limiting)
    REDIS_URL: z.string().url().optional(), // Alternative: redis://username:password@host:port/db
    REDIS_HOST: z.string().default("localhost"),
    REDIS_PORT: z.string().default("6379"),
    REDIS_USERNAME: z.string().optional(), // Redis 6+ ACL username
    REDIS_PASSWORD: z.string().optional(),
    REDIS_DB: z.string().default("0"), // Redis database number (0-15)
    REDIS_TLS: z.enum(["true", "false"]).optional().default("false"), // Enable TLS for internet exposure

    // Feature Flags (server-side)
    ENABLE_PRICING: z.enum(["true", "false"]).optional().default("false"), // Enable pricing page (server-side)

    // [DEPRECATED] Legifrance API - No longer used, search is now local via legal_articles table
    // Keep for backwards compatibility, will be removed in future version
    LEGIFRANCE_API_URL: z.string().url().optional(),
    LEGIFRANCE_API_TOKEN: z.string().optional(),
  },

  /**
   * Specify your client-side environment variables schema here. This way you can ensure the app
   * isn't built with invalid env vars. To expose them to the client, prefix them with
   * `NEXT_PUBLIC_`.
   */
  client: {
    NEXT_PUBLIC_APP_URL: z.string().url().optional(),
    NEXT_PUBLIC_UMAMI_WEBSITE_ID: z.string().optional(),
    NEXT_PUBLIC_ENABLE_PRICING: z
      .enum(["true", "false"])
      .optional()
      .default("false"),
  },

  /**
   * You can't destruct `process.env` as a regular object in the Next.js edge runtimes (e.g.
   * middlewares) or client-side so we need to destruct manually.
   */
  runtimeEnv: {
    SENTRY_DSN: process.env.SENTRY_DSN,
    DATABASE_URL: process.env.DATABASE_URL,
    DATABASE_CA_CERT: process.env.DATABASE_CA_CERT,
    DATABASE_CA_CERT_PATH: process.env.DATABASE_CA_CERT_PATH,
    DATABASE_SSL: process.env.DATABASE_SSL,
    NODE_ENV: process.env.NODE_ENV,
    BETTER_AUTH_SECRET: process.env.BETTER_AUTH_SECRET,
    // Use VERCEL_URL for preview deployments, fallback to BETTER_AUTH_URL for local dev
    BETTER_AUTH_URL: process.env.VERCEL_URL
      ? `https://${process.env.VERCEL_URL}`
      : (process.env.COOLIFY_URL ?? process.env.APP_URL),
    GOOGLE_CLIENT_ID: process.env.GOOGLE_CLIENT_ID,
    GOOGLE_CLIENT_SECRET: process.env.GOOGLE_CLIENT_SECRET,
    STRIPE_SECRET_KEY: process.env.STRIPE_SECRET_KEY,
    STRIPE_PUBLISHABLE_KEY: process.env.STRIPE_PUBLISHABLE_KEY,
    STRIPE_WEBHOOK_SECRET: process.env.STRIPE_WEBHOOK_SECRET,
    STRIPE_PRICE_SMALL: process.env.STRIPE_PRICE_SMALL,
    STRIPE_PRICE_STANDARD: process.env.STRIPE_PRICE_STANDARD,
    STRIPE_PRICE_LARGE: process.env.STRIPE_PRICE_LARGE,
    SMTP_HOST: process.env.SMTP_HOST,
    SMTP_PORT: process.env.SMTP_PORT,
    SMTP_SECURE: process.env.SMTP_SECURE,
    SMTP_USER: process.env.SMTP_USER,
    SMTP_PASS: process.env.SMTP_PASS,
    SMTP_FROM: process.env.SMTP_FROM,
    CONTACT_PRO_EMAIL: process.env.CONTACT_PRO_EMAIL,
    EMAIL_RATE_LIMIT: process.env.EMAIL_RATE_LIMIT,
    APP_URL: process.env.VERCEL_URL
      ? `https://${process.env.VERCEL_URL}`
      : (process.env.COOLIFY_URL ?? process.env.APP_URL),
    CRON_SECRET: process.env.CRON_SECRET,
    EMAIL_REPLY_DOMAIN: process.env.EMAIL_REPLY_DOMAIN,
    INVOICE_EMAIL_DOMAIN: process.env.INVOICE_EMAIL_DOMAIN,
    EMAIL_REPLY_API_KEY: process.env.EMAIL_REPLY_API_KEY,
    AI_SUMMARY_RATE_LIMIT_MINUTES: process.env.AI_SUMMARY_RATE_LIMIT_MINUTES,
    AI_SUMMARY_DAILY_LIMIT: process.env.AI_SUMMARY_DAILY_LIMIT,
    AI_SUMMARY_MIN_POSTS: process.env.AI_SUMMARY_MIN_POSTS,
    MAX_EXTERNAL_SUBSCRIBERS_PER_TOPIC:
      process.env.MAX_EXTERNAL_SUBSCRIBERS_PER_TOPIC,
    GUEST_VERIFY_TOKEN_EXPIRY_HOURS:
      process.env.GUEST_VERIFY_TOKEN_EXPIRY_HOURS,

    // S3
    S3_REGION: process.env.S3_REGION,
    S3_ENDPOINT: process.env.S3_ENDPOINT,
    S3_ACCESS_KEY_ID: process.env.S3_ACCESS_KEY_ID,
    S3_SECRET_ACCESS_KEY: process.env.S3_SECRET_ACCESS_KEY,
    S3_BUCKET: process.env.S3_BUCKET,
    S3_PUBLIC_URL: process.env.S3_PUBLIC_URL,

    // OVH AI
    OVH_AI_ENDPOINTS_EMBEDDING_MODEL_URL:
      process.env.OVH_AI_ENDPOINTS_EMBEDDING_MODEL_URL,
    OVH_AI_ENDPOINTS_ACCESS_TOKEN: process.env.OVH_AI_ENDPOINTS_ACCESS_TOKEN,
    OCR_PROVIDER: process.env.OCR_PROVIDER,
    UNSTRUCTURED_API_URL: process.env.UNSTRUCTURED_API_URL,
    UNSTRUCTURED_API_TOKEN: process.env.UNSTRUCTURED_API_TOKEN,
    UNSTRUCTURED_WEBHOOK_SECRET: process.env.UNSTRUCTURED_WEBHOOK_SECRET,
    DOCLING_API_URL: process.env.DOCLING_API_URL,
    DOCLING_API_TOKEN: process.env.DOCLING_API_TOKEN,
    DOCLING_WEBHOOK_SECRET: process.env.DOCLING_WEBHOOK_SECRET,
    DOCLING_TIMEOUT_MS: process.env.DOCLING_TIMEOUT_MS,
    RERANKER_URL: process.env.RERANKER_URL,
    RERANKER_API_TOKEN: process.env.RERANKER_API_TOKEN,

    // Redis
    REDIS_URL: process.env.REDIS_URL,
    REDIS_HOST: process.env.REDIS_HOST,
    REDIS_PORT: process.env.REDIS_PORT,
    REDIS_USERNAME: process.env.REDIS_USERNAME,
    REDIS_PASSWORD: process.env.REDIS_PASSWORD,
    REDIS_DB: process.env.REDIS_DB,
    REDIS_TLS: process.env.REDIS_TLS,

    NEXT_PUBLIC_APP_URL: process.env.APP_URL,
    NEXT_PUBLIC_UMAMI_WEBSITE_ID: process.env.NEXT_PUBLIC_UMAMI_WEBSITE_ID,
    NEXT_PUBLIC_ENABLE_PRICING: process.env.NEXT_PUBLIC_ENABLE_PRICING,
    ENABLE_PRICING: process.env.NEXT_PUBLIC_ENABLE_PRICING, // Server-side mirror of NEXT_PUBLIC_ENABLE_PRICING

    // Legifrance API
    LEGIFRANCE_API_URL: process.env.LEGIFRANCE_API_URL,
    LEGIFRANCE_API_TOKEN: process.env.LEGIFRANCE_API_TOKEN,
  },
  /**
   * Run `build` or `dev` with `SKIP_ENV_VALIDATION` to skip env validation. This is especially
   * useful for Docker builds.
   */
  skipValidation: !!process.env.SKIP_ENV_VALIDATION,
  /**
   * Makes it so that empty strings are treated as undefined. `SOME_VAR: z.string()` and
   * `SOME_VAR=''` will throw an error.
   */
  emptyStringAsUndefined: true,
});
